<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="anna-persona">
    <div class="anna-persona-head">
      <oxd-text tag="h6" class="anna-persona-title">
        {{ data ? data.title : '' }}
      </oxd-text>
      <oxd-text v-if="updatedAt" tag="p" class="anna-persona-meta">
        Last changed in Vapi {{ updatedAt }}
      </oxd-text>
    </div>

    <oxd-text v-if="data && data.error" tag="p" class="anna-persona-error">
      {{ data.error }}
    </oxd-text>
    <oxd-text v-if="optionsError" tag="p" class="anna-persona-note">
      {{ optionsError }}
    </oxd-text>
    <oxd-text
      v-for="d in deprecations"
      :key="d"
      tag="p"
      class="anna-persona-error"
    >
      {{ d }}
    </oxd-text>

    <template v-if="a">
      <div class="anna-tabs" role="tablist">
        <button
          v-for="t in visibleTabs"
          :key="t.key"
          type="button"
          role="tab"
          :aria-selected="tab === t.key"
          :class="['anna-tab', {'anna-tab--active': tab === t.key}]"
          @click="openTab(t.key)"
        >
          {{ t.label }}
        </button>
      </div>

      <!-- Model -->
      <div v-show="tab === 'model'" class="anna-tab-body">
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field v-model="a.name" label="Assistant name" />
          </oxd-grid-item>
          <oxd-grid-item v-if="!chat">
            <search-select
              label="First message mode"
              :options="enumOptions(options?.firstMessageModes)"
              :model-value="a.firstMessageMode ?? ''"
              placeholder="Vapi default"
              @update:model-value="setTop('firstMessageMode', $event)"
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="!chat" class="anna-persona-wide">
            <oxd-input-field
              v-model="a.firstMessage"
              label="First message (what she says when the interview starts)"
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="!managed" class="anna-persona-wide">
            <div class="anna-prompt-head">
              <oxd-text tag="p" class="anna-label"> Instructions </oxd-text>
              <button
                type="button"
                class="anna-link-button"
                @click="fullscreen = true"
              >
                <i class="bi bi-arrows-fullscreen" aria-hidden="true" /> Full
                screen
              </button>
            </div>
            <textarea
              v-model="systemPrompt"
              class="anna-prompt"
              spellcheck="true"
            ></textarea>
            <oxd-text tag="p" class="anna-persona-help">
              <template v-if="promptVariables.length">
                Placeholders in these instructions:
                {{ promptVariables.join(', ') }}. Vapi fills them in on each
                call from the values ANNA sends (not connected yet), and
                {{ braces('now') }}, {{ braces('customer.number') }} and the
                like itself.
              </template>
              <template v-else>
                No {{ braces('placeholders') }} in these instructions.
              </template>
            </oxd-text>
          </oxd-grid-item>
          <!-- ANNA's assistants get their instructions from ANNA's prompt library -->
          <oxd-grid-item v-else class="anna-persona-wide">
            <oxd-text tag="p" class="anna-persona-help">
              What this assistant says and does is set in its instructions below
              this card; ANNA sends them with every call or chat. Its own
              instructions and tools in Vapi are a fixed shell that ANNA keeps,
              so they aren't edited here.
            </oxd-text>
          </oxd-grid-item>
          <oxd-grid-item>
            <search-select
              label="Model provider"
              :options="providerOptions('model')"
              :model-value="a.model.provider || ''"
              :allow-custom="!options"
              @update:model-value="changeProvider('model', $event)"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <search-select
              label="Model"
              :options="enumOptions(spec('model').enums?.model)"
              :model-value="a.model.model || ''"
              :allow-custom="true"
              placeholder="Type or pick a model"
              @update:model-value="setIn('model', 'model', $event)"
            />
          </oxd-grid-item>
        </oxd-grid>
        <provider-fields
          :section="a.model"
          :spec="spec('model')"
          :skip="['model']"
          @set="(k, v) => setIn('model', k, v)"
        />
        <label class="anna-check">
          <input
            type="checkbox"
            :checked="!!a.model.emotionRecognitionEnabled"
            @change="
              setIn(
                'model',
                'emotionRecognitionEnabled',
                $event.target.checked || undefined,
              )
            "
          />
          Emotion recognition (the model hears the caller's tone)
        </label>
      </div>

      <!-- Voice -->
      <div v-show="tab === 'voice'" class="anna-tab-body">
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <search-select
              label="Voice provider"
              :options="providerOptions('voice')"
              :model-value="a.voice?.provider || ''"
              :allow-custom="!options"
              @update:model-value="changeProvider('voice', $event)"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <search-select
              label="Voice"
              :options="enumOptions(spec('voice').enums?.voiceId)"
              :model-value="a.voice?.voiceId || ''"
              :allow-custom="true"
              placeholder="Type or pick a voice id"
              @update:model-value="setIn('voice', 'voiceId', $event)"
            />
            <oxd-text tag="p" class="anna-persona-help">
              {{
                spec('voice').enums?.voiceId
                  ? "Vapi's preset voices for this provider. For any other voice, copy its id from Vapi's Voice Library."
                  : "Vapi has no preset list for this provider: copy the voice id from Vapi's Voice Library."
              }}
            </oxd-text>
          </oxd-grid-item>
        </oxd-grid>
        <provider-fields
          v-if="a.voice"
          :section="a.voice"
          :spec="spec('voice')"
          :skip="['voiceId']"
          @set="(k, v) => setIn('voice', k, v)"
        />
      </div>

      <!-- Transcriber -->
      <div v-show="tab === 'transcriber'" class="anna-tab-body">
        <oxd-text tag="p" class="anna-persona-help">
          How the candidate's speech is turned into text.
        </oxd-text>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <search-select
              label="Transcriber provider"
              :options="[
                {id: '', label: 'Vapi default'},
                ...providerOptions('transcriber'),
              ]"
              :model-value="a.transcriber?.provider || ''"
              placeholder="Vapi default"
              :allow-custom="!options"
              @update:model-value="changeProvider('transcriber', $event)"
            />
          </oxd-grid-item>
        </oxd-grid>
        <provider-fields
          v-if="a.transcriber"
          :section="a.transcriber"
          :spec="spec('transcriber')"
          @set="(k, v) => setIn('transcriber', k, v)"
        />
      </div>

      <!-- Call behaviour -->
      <div v-show="tab === 'call'" class="anna-tab-body">
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item class="anna-persona-wide">
            <oxd-input-field
              v-model="a.endCallMessage"
              label="Closing message"
            />
          </oxd-grid-item>
          <oxd-grid-item class="anna-persona-wide">
            <oxd-input-field
              :model-value="(a.endCallPhrases || []).join('\n')"
              type="textarea"
              label="End-call phrases (one per line: she hangs up after saying one)"
              @update:model-value="setLines('endCallPhrases', $event)"
            />
          </oxd-grid-item>
          <oxd-grid-item class="anna-persona-wide">
            <oxd-input-field
              v-model="a.voicemailMessage"
              label="Voicemail message (left when the call reaches voicemail)"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :model-value="String(a.maxDurationSeconds ?? '')"
              :label="`Maximum call length, seconds (${
                options?.maxDurationSeconds?.min ?? 10
              }–${options?.maxDurationSeconds?.max ?? 43200})`"
              placeholder="Vapi default · 600"
              @update:model-value="setNumber('maxDurationSeconds', $event)"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <search-select
              label="Background sound (or an audio URL)"
              :options="enumOptions(options?.backgroundSounds)"
              :model-value="
                typeof a.backgroundSound === 'string' ? a.backgroundSound : ''
              "
              placeholder="Vapi default"
              :allow-custom="true"
              @update:model-value="setTop('backgroundSound', $event)"
            />
          </oxd-grid-item>
          <oxd-grid-item v-for="f in planNumbers" :key="f.path">
            <oxd-input-field
              :model-value="String(getPath(f.path) ?? '')"
              :label="f.label"
              :placeholder="`Vapi default · ${f.min}–${f.max}`"
              @update:model-value="setPlanNumber(f, $event)"
            />
          </oxd-grid-item>
        </oxd-grid>
        <label class="anna-check">
          <input
            type="checkbox"
            :checked="!!a.firstMessageInterruptionsEnabled"
            @change="
              setTop(
                'firstMessageInterruptionsEnabled',
                $event.target.checked || undefined,
              )
            "
          />
          The candidate can interrupt the first message
        </label>
      </div>

      <!-- Recording & privacy -->
      <div v-show="tab === 'recording'" class="anna-tab-body">
        <label v-for="f in planChecks" :key="f.path" class="anna-check">
          <input
            type="checkbox"
            :checked="!!getPath(f.path)"
            @change="setPath(f.path, $event.target.checked)"
          />
          {{ f.label }}
        </label>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <search-select
              label="Recording format"
              :options="enumOptions(['wav;l16', 'mp3'])"
              :model-value="getPath('artifactPlan.recordingFormat') ?? ''"
              placeholder="Vapi default"
              @update:model-value="
                setPath('artifactPlan.recordingFormat', $event || undefined)
              "
            />
          </oxd-grid-item>
        </oxd-grid>
      </div>

      <!-- Advanced: the whole assistant -->
      <div v-show="tab === 'advanced'" class="anna-tab-body">
        <oxd-text tag="p" class="anna-persona-help">
          Everything Vapi lets you change on this assistant, as JSON: tools,
          hooks, server, analysis and compliance plans, speaking plans, and
          anything the tabs above don't show. Apply, then Save to Vapi. A
          section you change here is replaced whole in Vapi.
        </oxd-text>
        <textarea
          v-model="json"
          class="anna-prompt anna-json"
          spellcheck="false"
        ></textarea>
        <oxd-text v-if="jsonError" tag="p" class="anna-field-error">{{
          jsonError
        }}</oxd-text>
        <div class="anna-persona-actions anna-persona-actions--left">
          <oxd-button
            display-type="ghost"
            label="Apply JSON"
            @click="applyJson"
          />
        </div>
      </div>
    </template>

    <div class="anna-persona-actions">
      <oxd-text v-if="dirty" tag="p" class="anna-persona-meta"
        >Unsaved changes</oxd-text
      >
      <oxd-button
        display-type="ghost"
        label="Reload from Vapi"
        :disabled="busy"
        @click="load"
      />
      <oxd-button
        v-if="a"
        display-type="secondary"
        label="Save to Vapi"
        :disabled="busy || !dirty"
        @click="save"
      />
    </div>

    <!-- Full-screen prompt -->
    <div
      v-if="fullscreen"
      class="anna-fullscreen"
      role="dialog"
      aria-modal="true"
      @keydown.esc="fullscreen = false"
    >
      <div class="anna-fullscreen-head">
        <oxd-text tag="h6" class="anna-persona-title">
          {{ data?.title }} · instructions
        </oxd-text>
        <span class="anna-persona-meta"
          >{{ systemPrompt.length }} characters</span
        >
        <oxd-button
          display-type="secondary"
          label="Done"
          @click="fullscreen = false"
        />
      </div>
      <textarea
        ref="fullscreenText"
        v-model="systemPrompt"
        class="anna-fullscreen-text"
        spellcheck="true"
      ></textarea>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import SearchSelect from './SearchSelect.vue';
import ProviderFields from './ProviderFields.vue';

// Loaded once per page for both personas.
let optionsRequest = null;

// Model settings that don't depend on the provider, kept when it changes.
const MODEL_COMMON = [
  'messages',
  'temperature',
  'maxTokens',
  'toolIds',
  'tools',
  'emotionRecognitionEnabled',
  'numFastTurns',
];

export default {
  name: 'PersonaEditor',
  components: {
    'search-select': SearchSelect,
    'provider-fields': ProviderFields,
  },
  props: {
    personaSlot: {
      type: String,
      required: true,
    },
    // a text (chat) assistant: no first message, voice, transcriber, call or recording
    chat: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/personas',
    );
    return {http};
  },
  data() {
    return {
      busy: false,
      data: null,
      a: null, // the assistant being edited (everything Vapi lets you change)
      saved: '', // JSON of `a` as loaded, for the unsaved-changes check
      options: null,
      optionsError: '',
      tab: 'model',
      tabs: [
        {key: 'model', label: 'Model'},
        {key: 'voice', label: 'Voice'},
        {key: 'transcriber', label: 'Transcriber'},
        {key: 'call', label: 'Call'},
        {key: 'recording', label: 'Recording & privacy'},
        {key: 'advanced', label: 'Advanced (JSON)'},
      ],
      json: '',
      jsonError: '',
      fullscreen: false,
      planNumbers: [
        {
          path: 'startSpeakingPlan.waitSeconds',
          label: 'Wait before replying, seconds',
          min: 0,
          max: 5,
        },
        {
          path: 'stopSpeakingPlan.numWords',
          label: 'Words before she stops for the candidate',
          min: 0,
          max: 10,
        },
        {
          path: 'stopSpeakingPlan.voiceSeconds',
          label: 'Speech before she stops, seconds',
          min: 0,
          max: 0.5,
        },
        {
          path: 'stopSpeakingPlan.backoffSeconds',
          label: 'Pause after being interrupted, seconds',
          min: 0,
          max: 10,
        },
      ],
      planChecks: [
        {path: 'artifactPlan.recordingEnabled', label: 'Record the call'},
        {
          path: 'artifactPlan.transcriptPlan.enabled',
          label: 'Keep a transcript',
        },
        {
          path: 'compliancePlan.hipaaEnabled',
          label: 'HIPAA mode (Vapi stores no recording or transcript)',
        },
        {path: 'compliancePlan.pciEnabled', label: 'PCI mode'},
      ],
    };
  },
  computed: {
    // the assistants whose prompt is ANNA's (their instructions below the card), not edited here
    managed() {
      return ['general', 'cv', 'reply', 'mail', 'review'].includes(
        this.personaSlot,
      );
    },
    visibleTabs() {
      return this.chat
        ? this.tabs.filter((t) => ['model', 'advanced'].includes(t.key))
        : this.tabs;
    },
    updatedAt() {
      const at = this.data?.persona?.updatedAt;
      return at ? new Date(at).toLocaleString() : '';
    },
    deprecations() {
      const d = this.data?.persona?.modelDeprecations;
      if (!d) return [];
      return (Array.isArray(d) ? d : [d]).map((x) =>
        typeof x === 'string' ? x : x.message || JSON.stringify(x),
      );
    },
    dirty() {
      return !!this.a && JSON.stringify(this.a) !== this.saved;
    },
    systemPrompt: {
      get() {
        return (
          this.a?.model?.messages?.find((m) => m.role === 'system')?.content ??
          ''
        );
      },
      set(value) {
        const messages = [...(this.a.model.messages || [])];
        const i = messages.findIndex((m) => m.role === 'system');
        if (i >= 0) messages[i] = {...messages[i], content: value};
        else messages.unshift({role: 'system', content: value});
        this.a.model = {...this.a.model, messages};
      },
    },
    promptVariables() {
      const text = `${this.systemPrompt}\n${this.a?.firstMessage || ''}`;
      return [...new Set(text.match(/\{\{\s*[\w.]+\s*\}\}/g) || [])].map((v) =>
        v.replace(/\s+/g, ''),
      );
    },
  },
  watch: {
    fullscreen(open) {
      if (open) this.$nextTick(() => this.$refs.fullscreenText?.focus());
    },
  },
  created() {
    this.load();
    this.loadOptions();
  },
  methods: {
    apply(data) {
      this.data = data;
      const assistant = data.persona?.assistant;
      this.a = assistant ? JSON.parse(JSON.stringify(assistant)) : null;
      if (this.a && !this.a.model) this.a.model = {};
      this.saved = this.a ? JSON.stringify(this.a) : '';
      this.jsonError = '';
    },
    load() {
      this.busy = true;
      this.http
        .request({
          method: 'GET',
          url: `/api/v2/anna/personas/${this.personaSlot}`,
        })
        .then((response) => this.apply(response.data.data))
        .finally(() => {
          this.busy = false;
        });
    },
    loadOptions() {
      optionsRequest =
        optionsRequest ||
        this.http
          .request({method: 'GET', url: '/api/v2/anna/vapi/options'})
          .then((r) => r.data.data);
      optionsRequest
        .then((options) => {
          this.options = options;
          if (options.stale)
            this.optionsError =
              "Vapi's model and voice lists are from an earlier copy: Vapi could not be reached.";
        })
        .catch(() => {
          optionsRequest = null;
          this.optionsError =
            "Vapi's model and voice lists could not be loaded, so type the values.";
        });
    },
    save() {
      this.busy = true;
      this.http
        .request({
          method: 'PUT',
          url: `/api/v2/anna/personas/${this.personaSlot}`,
          data: {assistant: this.a},
        })
        .then((response) => {
          this.apply(response.data.data);
          return this.$toast.saveSuccess();
        })
        // a Vapi or validation error is shown by the API client's error toast
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
    openTab(key) {
      if (key === 'advanced') {
        this.json = JSON.stringify(this.a, null, 2);
        this.jsonError = '';
      }
      this.tab = key;
    },
    applyJson() {
      let parsed;
      try {
        parsed = JSON.parse(this.json);
      } catch (e) {
        this.jsonError = `Not valid JSON: ${e.message}`;
        return;
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        this.jsonError = 'The assistant must be a JSON object.';
        return;
      }
      const unknown = this.options
        ? Object.keys(parsed).filter(
            (k) => !this.options.updatableFields.includes(k),
          )
        : [];
      if (unknown.length) {
        this.jsonError = `Vapi doesn't let you change: ${unknown.join(', ')}.`;
        return;
      }
      this.a = {model: {}, ...parsed};
      this.jsonError = '';
      this.$toast.info({
        title: 'Applied',
        message: 'Now Save to Vapi to keep it.',
      });
    },
    braces(name) {
      return `{{${name}}}`;
    },
    // ---- options
    spec(part) {
      const provider = this.a?.[part]?.provider;
      return (
        this.options?.[part]?.providers?.[provider] || {enums: {}, numbers: {}}
      );
    },
    providerOptions(part) {
      return Object.keys(this.options?.[part]?.providers || {}).map((p) => ({
        id: p,
        label: p,
      }));
    },
    enumOptions(values) {
      return (values || []).map((v) => ({id: v, label: v}));
    },
    // ---- editing
    setTop(key, value) {
      if (value === '' || value === undefined) delete this.a[key];
      else this.a[key] = value;
    },
    setIn(part, key, value) {
      const next = {...(this.a[part] || {})};
      if (value === '' || value === undefined) delete next[key];
      else next[key] = value;
      this.a[part] = next;
    },
    setNumber(key, raw) {
      const text = String(raw ?? '').trim();
      if (text === '') return this.setTop(key, undefined);
      const value = Number(text);
      if (!Number.isNaN(value)) this.setTop(key, value);
    },
    setLines(key, text) {
      const lines = String(text || '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      this.setTop(key, lines.length ? lines : undefined);
    },
    // Provider-specific settings don't carry over to another provider.
    changeProvider(part, provider) {
      if (!provider) {
        delete this.a[part];
        return;
      }
      if (provider === this.a[part]?.provider) return;
      const current = this.a[part] || {};
      const kept =
        part === 'model'
          ? Object.fromEntries(
              MODEL_COMMON.filter((k) => k in current).map((k) => [
                k,
                current[k],
              ]),
            )
          : {};
      this.a[part] = {...kept, provider};
    },
    getPath(path) {
      return path
        .split('.')
        .reduce((o, k) => (o == null ? undefined : o[k]), this.a);
    },
    // Sets a nested value, creating plan objects as needed; undefined removes it.
    setPath(path, value) {
      const keys = path.split('.');
      const top = keys[0];
      const root = JSON.parse(JSON.stringify(this.a[top] || {}));
      let node = root;
      keys.slice(1, -1).forEach((k) => {
        node[k] = node[k] && typeof node[k] === 'object' ? node[k] : {};
        node = node[k];
      });
      const last = keys[keys.length - 1];
      if (value === undefined) delete node[last];
      else node[last] = value;
      this.a[top] = root;
    },
    setPlanNumber(field, raw) {
      const text = String(raw ?? '').trim();
      if (text === '') return this.setPath(field.path, undefined);
      const value = Number(text);
      if (!Number.isNaN(value) && value >= field.min && value <= field.max)
        this.setPath(field.path, value);
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-persona {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #e7ebf1;
  border-radius: 12px;
  background: #fbfcfd;
}
.anna-persona-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.anna-persona-title {
  font-size: 14px;
  font-weight: 700;
  color: #12233f;
}
.anna-persona-meta,
.anna-persona-help {
  font-size: 12px;
  color: #9aa4b1;
}
.anna-persona-help {
  margin-top: 4px;
}
.anna-persona-error,
.anna-persona-note {
  font-size: 13px;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.anna-persona-error {
  color: #b8860b;
  background: #fdf1de;
}
.anna-persona-note {
  color: #5b6672;
  background: #f2f4f7;
}
.anna-persona-wide {
  grid-column: 1 / -1;
}
.anna-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid #e7ebf1;
  margin-bottom: 14px;
}
.anna-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 9px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  color: #6a7482;
  cursor: pointer;
  &--active {
    color: #1f5fa8;
    border-bottom-color: #1f5fa8;
  }
}
.anna-tab-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.anna-label {
  font-size: 12px;
  font-weight: 700;
  color: #5b6672;
}
.anna-prompt-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.anna-link-button {
  background: none;
  border: none;
  color: #1f5fa8;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.anna-prompt {
  width: 100%;
  box-sizing: border-box;
  min-height: 260px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid #dde3ea;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  color: #16202b;
  background: #ffffff;
  &:focus {
    outline: none;
    border-color: #1f5fa8;
  }
}
.anna-json {
  min-height: 420px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}
.anna-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #16202b;
  cursor: pointer;
}
.anna-field-error {
  font-size: 12px;
  color: #c0392b;
}
.anna-persona-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  &--left {
    justify-content: flex-start;
    margin-top: 0;
  }
}
.anna-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  padding: 20px 32px 28px;
  gap: 12px;
}
.anna-fullscreen-head {
  display: flex;
  align-items: center;
  gap: 16px;
  .anna-persona-title {
    flex: 1;
  }
}
.anna-fullscreen-text {
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  resize: none;
  padding: 18px 22px;
  border: 1px solid #dde3ea;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.7;
  color: #16202b;
  &:focus {
    outline: none;
    border-color: #1f5fa8;
  }
}
</style>
